import React, { useState, useEffect } from "react";
import Layout from "../../layout/Main/Layout";
import CustomTable from "../../components/Custom/Table/CustomTable";
import { get, del, patch } from "../../config/axios";
import { 
  Typography, 
  Box, 
  Chip, 
  IconButton, 
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import { 
  Delete as DeleteIcon, 
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon
} from "@mui/icons-material";
import { useDebouncedValue } from "../../helper/debounce";
import Searchbar from "../../components/Custom/SearchBar/Searchbar";
import { toastMessage } from "../../utils/toastMessage";
import moment from "moment";

const DietFeedback = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const debouncedSearch = useDebouncedValue(search, 500);
  
  // Filter states
  const [planTypeFilter, setPlanTypeFilter] = useState("");
  const [ratingFilter, setRatingFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // Statistics
  const [statistics, setStatistics] = useState({
    totalFeedback: 0,
    averageRating: 0,
    planTypeDistribution: [],
    ratingDistribution: []
  });
  
  // View feedback dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  useEffect(() => {
    if (search === "") {
      fetchFeedbacks();
    } else if (debouncedSearch) {
      fetchFeedbacks();
    }
  }, [search, debouncedSearch, page, planTypeFilter, ratingFilter, startDate, endDate]);

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      
      let queryParams = `page=${page}&limit=${perPage}`;
      
      if (search) queryParams += `&search=${search}`;
             if (planTypeFilter) queryParams += `&dietType=${planTypeFilter}`;
      if (ratingFilter) queryParams += `&rating=${ratingFilter}`;
      if (startDate) queryParams += `&startDate=${startDate}`;
      if (endDate) queryParams += `&endDate=${endDate}`;

      const res = await get(`/api/dashboard/diet-feedback/all?${queryParams}`);
      
      console.log("API Response:", res);
      console.log("Response data:", res.data);
      console.log("Response statistics:", res.statistics);
      console.log("First feedback mealPlan:", res.data[0]?.mealPlan);
      console.log("First feedback dietType:", res.data[0]?.mealPlan?.dietType);
      console.log("All feedbacks:", res.data.map(f => ({ id: f._id, dietType: f.mealPlan?.dietType, planType: f.planType })));
      
      if (res.data && Array.isArray(res.data)) {
        const mappedData = res.data.map((item) => ({
          ...item,
          action: { 
            view: true, 
            delete: true 
          },
        }));
        
        setFeedbacks(mappedData);
        setPageCount(res.totalPage || 1);
        setTotalData(res.totalData || 0);
        setPerPage(res.perPage || 10);
        setCurrentPage(res.currentPage || 1);
        
        if (res.statistics) {
          setStatistics(res.statistics);
        }
      }
    } catch (err) {
      console.error("Error:", err);
      toastMessage("Error fetching feedbacks. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (searchText) => {
    setSearch(searchText.trim());
    setPage(1);
  };

  const handleChange = (page) => {
    setPage(page);
  };

  const handleDelete = async (feedbackId) => {
    if (window.confirm("Are you sure you want to delete this feedback?")) {
      try {
        await del(`/api/dashboard/diet-feedback/${feedbackId}`);
        toastMessage("Feedback deleted successfully", "success");
        fetchFeedbacks();
      } catch (error) {
        toastMessage("Error deleting feedback", "error");
      }
    }
  };

  const handleViewFeedback = (feedback) => {
    setSelectedFeedback(feedback);
    setViewDialogOpen(true);
  };

  const handleStatusToggle = async (feedbackId, currentStatus) => {
    try {
      await patch(`/api/dashboard/diet-feedback/${feedbackId}/status`, {
        active: !currentStatus
      });
      toastMessage(`Feedback ${!currentStatus ? 'activated' : 'deactivated'} successfully`, "success");
      fetchFeedbacks();
    } catch (error) {
      toastMessage("Error updating feedback status", "error");
    }
  };

  const resetFilters = () => {
    setPlanTypeFilter("");
    setRatingFilter("");
    setStartDate("");
    setEndDate("");
    setSearch("");
    setPage(1);
  };

  const getPlanTypeDisplay = (planType) => {
    return planType === "traditional" ? "Traditional" : "Low FODMAP";
  };

  const getPlanTypeColor = (planType) => {
    return planType === "traditional" ? "primary" : "secondary";
  };

  const feedbackTableColumns = [
    {
      id: "S.No",
      label: "S.No",
      minWidth: 70,
      align: "left",
      format: (value, index) => index + 1,
    },
    {
      id: "feedbackDate",
      label: "Date",
      minWidth: 120,
      align: "left",
      format: (value) => moment(value).format("DD/MM/YYYY"),
    },
    {
      id: "patient.fullname",
      label: "User Name",
      minWidth: 150,
      align: "left",
    },
    {
      id: "mealPlan.dietType",
      label: "Diet Type",
      minWidth: 150,
      align: "center",
    },
    {
      id: "rating",
      label: "Rating (1-5)",
      minWidth: 120,
      align: "center",
    },
    {
      id: "comments",
      label: "Comments (optional)",
      minWidth: 200,
      align: "left",
    },
    {
      id: "action",
      label: "Actions",
      minWidth: 120,
      align: "center",
    },
  ];

  return (
    <Layout>
      <div style={{ padding: "1rem" }}>
        <Typography variant="h5" gutterBottom>
          Diet Feedback Management
        </Typography>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Feedback
                </Typography>
                <Typography variant="h4">
                  {statistics.totalFeedback}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Average Rating
                </Typography>
                                 <Typography variant="h4">
                   {(statistics.averageRating || 0).toFixed(1)}/5
                 </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Traditional Plans
                </Typography>
                <Typography variant="h4">
                  {statistics.planTypeDistribution.find(p => p._id === "traditional")?.count || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Low FODMAP Plans
                </Typography>
                <Typography variant="h4">
                  {statistics.planTypeDistribution.find(p => p._id === "low_fodmap")?.count || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Filters
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <Searchbar
                  search={handleSearch}
                  placeholder="Search by comments"
                  debounceTime={1000}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Plan Type</InputLabel>
                  <Select
                    value={planTypeFilter}
                    label="Plan Type"
                    onChange={(e) => setPlanTypeFilter(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="traditional">Traditional</MenuItem>
                    <MenuItem value="low_fodmap">Low FODMAP</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Rating</InputLabel>
                  <Select
                    value={ratingFilter}
                    label="Rating"
                    onChange={(e) => setRatingFilter(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="5">5 Stars</MenuItem>
                    <MenuItem value="4">4 Stars</MenuItem>
                    <MenuItem value="3">3 Stars</MenuItem>
                    <MenuItem value="2">2 Stars</MenuItem>
                    <MenuItem value="1">1 Star</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Start Date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="End Date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={1}>
                <Button
                  variant="outlined"
                  onClick={resetFilters}
                  startIcon={<RefreshIcon />}
                  fullWidth
                >
                  Reset
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Feedback Table */}
        <CustomTable
          data={feedbacks}
          columns={feedbackTableColumns}
          handlePageChange={(page) => handleChange(page)}
          loading={loading}
          pageCount={pageCount}
          pageNumber={page}
          totalData={totalData}
          perPage={perPage}
          currentPage={currentPage}
          customRender={{
            "mealPlan.dietType": (value) => (
              <Chip
                label={getPlanTypeDisplay(value)}
                color={getPlanTypeColor(value)}
                size="small"
                variant="outlined"
              />
            ),
            "rating": (value) => (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Rating value={value} readOnly size="small" />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  ({value}/5)
                </Typography>
              </Box>
            ),
            "action": (value, row) => (
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Tooltip title="View Details">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleViewFeedback(row)}
                  >
                    <ViewIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(row._id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            ),
          }}
        />

        {search && feedbacks.length === 0 && !loading && (
          <Typography variant="body1" sx={{ color: 'red', textAlign: 'center', mt: 2 }}>
            No feedbacks found for your search.
          </Typography>
        )}

        {/* View Feedback Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Feedback Details</DialogTitle>
          <DialogContent>
            {selectedFeedback && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      User Name
                    </Typography>
                    <Typography variant="body1">
                      {selectedFeedback.patient?.fullname || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Plan Type
                    </Typography>
                    <Chip
                      label={getPlanTypeDisplay(selectedFeedback.planType)}
                      color={getPlanTypeColor(selectedFeedback.planType)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Rating
                    </Typography>
                    <Rating value={selectedFeedback.rating} readOnly />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Date
                    </Typography>
                    <Typography variant="body1">
                      {moment(selectedFeedback.feedbackDate).format("DD/MM/YYYY HH:mm")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Comments
                    </Typography>
                    <Typography variant="body1">
                      {selectedFeedback.comments || "No comments provided"}
                    </Typography>
                  </Grid>
                  {selectedFeedback.symptoms && selectedFeedback.symptoms.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Symptoms
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {selectedFeedback.symptoms.map((symptom, index) => (
                          <Chip key={index} label={symptom} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DietFeedback;
